/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { type ReactNode, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import LinkIcon from '@atlaskit/icon/core/link';
import MentionIcon from '@atlaskit/icon/core/mention';
import { token } from '@atlaskit/tokens';
import type { SearchLinkedResult } from '@atlassian/search-client/common/types';
import type { SearchPageNode } from '@atlassian/search-client/search-page/types';

import { EVENT_SOURCE } from '../../constants/analytics';
import type { ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import type { ResultClickedCallback } from '../../constants/types';
import { reorderLinksByProduct } from '../../utils/link-attachments';
import { Expand, ExpandList } from '../expand-list';
import { getLinksFromText, LinkAttachments } from '../link-attachments';
import { type SearchResultProps } from '../search-result';
import type { SearchResultTypeMessageKey } from '../search-result-type/messages';

import { LinkedResult } from './linked-result';
import { messages } from './messages';

enum Category {
	MENTIONS = 'MENTIONS',
	MENTIONEDBY = 'MENTIONEDBY',
	SIMILAR = 'SIMILAR',
	CHILD = 'CHILD',
}

/**
 * Deduplicates URLs by removing any links that already exist in linkedResults
 * @param links - Array of URL strings
 * @param linkedResults - Array of linked result objects that have a url property
 * @returns Array of deduplicated URL strings
 */
const deduplicateLinksAgainstLinkedResults = (
	links: string[],
	linkedResults?: Array<{ url: string }> | null,
): string[] => {
	if (!linkedResults || linkedResults.length === 0) {
		return links;
	}
	const linkedResultUrls = new Set(linkedResults.map((result) => result.url));

	// Filter out links that exist in linkedResults
	return links.filter((link) => !linkedResultUrls.has(link));
};

const linkedResultsStyles = cssMap({
	base: {
		position: 'relative',
		paddingTop: token('space.050'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
	},
	showMoreButton: {
		alignSelf: 'flex-start',
	},
});

export type LinkedResultsProps = {
	links?: string[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined | null;
	onResultContextMenu?: ResultClickedCallback;
	showSeeAllButton?: boolean;
	product?: string;
	fallbackType?: SearchResultTypeMessageKey;
};

export const LinkedResults: (
	props: LinkedResultsProps & SearchResultProps,
) => JSX.Element | null = (props: LinkedResultsProps & SearchResultProps) => {
	const {
		links,
		linkedResults,
		url: parentUrl,
		thirdPartyConfigs,
		onResultClicked,
		onResultContextMenu,
		position,
		showSeeAllButton,
		product,
		fallbackType,
	} = props;

	const [showAll, setShowAll] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Deduplicate links against linkedResults first, then reorder
	const deduplicatedLinks = deduplicateLinksAgainstLinkedResults(links ?? [], linkedResults);
	const orderedLinks = reorderLinksByProduct(deduplicatedLinks);

	const linkedResultsCount = linkedResults?.length || 0;
	const linksCount = orderedLinks?.length || 0;
	const totalItemsCount = linkedResultsCount + linksCount;

	if (totalItemsCount === 0) {
		return null;
	}

	const initialResults = 2;
	const maxExpandedResults = 5;
	const hasMoreInitialResults = totalItemsCount > initialResults;
	const hasMoreExpandedResults = totalItemsCount > maxExpandedResults;

	const maxItemsToShow = showAll ? maxExpandedResults : initialResults;

	// Show linkedResults first, then links, respecting the total limit
	const linkedResultsToShow =
		linkedResults?.slice(0, Math.min(linkedResultsCount, maxItemsToShow)) || [];
	const remainingSlots = maxItemsToShow - linkedResultsToShow.length;
	const linksToShow = remainingSlots > 0 ? orderedLinks?.slice(0, remainingSlots) || [] : [];

	const components = [
		...linkedResultsToShow.map((linkedResult: SearchLinkedResult, index: any) => {
			return (
				<div key={`${linkedResult.id}-${index}`}>
					<LinkedResult
						linkedResult={linkedResult}
						thirdPartyConfigs={thirdPartyConfigs}
						category={linkedResult.category}
						sublistPosition={index}
						onResultClicked={onResultClicked}
						onResultContextMenu={onResultContextMenu}
						product={product}
						fallbackType={fallbackType}
					/>
				</div>
			);
		}),

		// Render LinkAttachments component if there are links to show
		...(linksToShow.length > 0
			? [
					<div key="link-attachments">
						<LinkAttachments
							links={linksToShow}
							isLinkedResults
							indexOffset={linkedResultsToShow.length}
							parentPosition={position}
							onResultClicked={onResultClicked}
							onResultContextMenu={onResultContextMenu}
						/>
					</div>,
				]
			: []),
	];

	const remainingCount = showAll
		? totalItemsCount - maxExpandedResults
		: totalItemsCount - initialResults;

	const showMoreX = Math.min(maxExpandedResults - initialResults, remainingCount);

	return (
		<div css={[linkedResultsStyles.base]}>
			{components}
			{hasMoreInitialResults && !showAll && (
				<div css={[linkedResultsStyles.showMoreButton]}>
					<Button
						onClick={() => {
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									action: 'clicked',
									actionSubject: `searchPageLinkedResultShowMoreButton`,
									source: EVENT_SOURCE,
									attributes: {
										moreShownCount: showMoreX,
									},
								},
							}).fire();
							return setShowAll(true);
						}}
						spacing="compact"
						// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
					>
						Show {showMoreX} more
					</Button>
				</div>
			)}
			{showSeeAllButton && showAll && hasMoreExpandedResults && (
				<div css={[linkedResultsStyles.showMoreButton]}>
					<Button
						onClick={() => {
							onResultClicked?.({ fromLinksSeeAll: true });
							return window.open(parentUrl, '_blank');
						}}
						spacing="compact"
						// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
					>
						See all <ArrowUpRightIcon label="External link" />
					</Button>
				</div>
			)}
		</div>
	);
};

type MentionedByLinkedResultsProps = {
	linkedResults: SearchPageNode;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined | null;
	partial: SearchPageNode;
	url: string;
	position?: number;
	onResultClicked: ResultClickedCallback;
	onResultContextMenu?: ResultClickedCallback;
	isAllProductSelected?: boolean;
};

export const MentionedByLinkedResults: ({
	linkedResults,
	thirdPartyConfigs,
	partial,
	url,
	position,
	onResultClicked,
	onResultContextMenu,
	isAllProductSelected,
}: MentionedByLinkedResultsProps) => any = ({
	linkedResults,
	thirdPartyConfigs,
	partial,
	url,
	position,
	onResultClicked,
	onResultContextMenu,
	isAllProductSelected,
}: MentionedByLinkedResultsProps): any => {
	const { formatMessage } = useIntl();

	const shouldShowLinkedResults =
		partial.linkedResults && partial.linkedResults.length > 0 && isAllProductSelected;

	// expanded linked results for the first 5 results by default
	const shouldExpandLinkedResults =
		position !== undefined && position <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND ? 0 : undefined;
	const mentionedByLinkedResults: SearchLinkedResult[] | undefined = linkedResults.filter(
		(linkedResult: { category: string }) =>
			linkedResult.category.toLocaleUpperCase() === Category.MENTIONEDBY,
	);

	return (
		shouldShowLinkedResults && (
			<ExpandList
				defaultExpandedIndex={shouldExpandLinkedResults}
				parentPosition={position}
				buttonType={Category.MENTIONEDBY}
			>
				<Expand
					icon={MentionIcon}
					attachmentCount={mentionedByLinkedResults?.length ?? 0}
					attachmentType="url"
					label={formatMessage(messages.mentionedIn)}
				>
					<LinkedResults
						{...partial}
						url={url}
						icon={<></>}
						thirdPartyConfigs={thirdPartyConfigs}
						linkedResults={mentionedByLinkedResults}
						onSeeAllClicked={onResultClicked}
						onResultClicked={onResultClicked}
						onResultContextMenu={onResultContextMenu}
						position={position}
					/>
				</Expand>
			</ExpandList>
		)
	);
};

type MentionsLinkedResultsProps = {
	linkedResults: SearchPageNode;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined | null;
	partial: SearchPageNode;
	url: string;
	position?: number;
	onResultClicked: ResultClickedCallback;
	onResultContextMenu?: ResultClickedCallback;
	isAllProductSelected?: boolean;
	additionalExpands?: ReactNode;
};

export const LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND = 4;
export const LINKED_RESULTS_DEFAULT_EXPAND_SIZE = 2;
export const MentionsLinkedResults: ({
	linkedResults,
	thirdPartyConfigs,
	partial,
	url,
	position,
	onResultClicked,
	onResultContextMenu,
	isAllProductSelected,
	additionalExpands,
}: MentionsLinkedResultsProps) => any = ({
	linkedResults,
	thirdPartyConfigs,
	partial,
	url,
	position,
	onResultClicked,
	onResultContextMenu,
	isAllProductSelected,
	additionalExpands,
}: MentionsLinkedResultsProps): any => {
	const { formatMessage } = useIntl();
	const extractedLinksFromText = getLinksFromText(partial.description);
	const hasLinkedResults = partial.linkedResults && partial.linkedResults.length > 0;
	const hasExtractedLinks = extractedLinksFromText && extractedLinksFromText.length > 0;
	const mentionsLinkedResults = linkedResults.filter(
		(linkedResult: { category: string }) =>
			linkedResult.category.toLocaleUpperCase() === Category.MENTIONS,
	);
	// expanded linked results for the first 5 results by default
	const shouldExpandLinkedResults =
		position !== undefined && position <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND ? 0 : undefined;

	const shouldShowLinkedResults = (hasExtractedLinks || hasLinkedResults) && isAllProductSelected;
	const totalLinksLength = (mentionsLinkedResults?.length ?? 0) + extractedLinksFromText.length;

	const hasAdditionalExpands = !!additionalExpands;
	// Only auto-expand when linked results are shown; additionalExpands (e.g. PRs) should start collapsed
	const defaultExpandedIndex = shouldShowLinkedResults ? shouldExpandLinkedResults : undefined;

	return (
		(shouldShowLinkedResults || hasAdditionalExpands) && (
			<ExpandList
				defaultExpandedIndex={defaultExpandedIndex}
				parentPosition={position}
				buttonType={Category.MENTIONS}
			>
				{shouldShowLinkedResults && (
					<Expand
						icon={LinkIcon}
						attachmentCount={totalLinksLength}
						attachmentType="url"
						label={formatMessage(messages.related)}
					>
						<LinkedResults
							{...partial}
							links={extractedLinksFromText}
							linkedResults={mentionsLinkedResults}
							url={url}
							icon={<></>}
							thirdPartyConfigs={thirdPartyConfigs}
							onResultClicked={onResultClicked}
							onResultContextMenu={onResultContextMenu}
							position={position}
							showSeeAllButton
						/>
					</Expand>
				)}
				{additionalExpands}
			</ExpandList>
		)
	);
};

type SimilarLinkedResultsProps = {
	linkedResults: SearchPageNode;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined | null;
	partial: SearchPageNode;
	url: string;
	position?: number;
	onResultClicked: ResultClickedCallback;
	isAllProductSelected?: boolean;
	onResultContextMenu?: ResultClickedCallback;
	is3pGroupingEnabled?: boolean;
	product?: string;
	fallbackType?: SearchResultTypeMessageKey;
};

export const SimilarLinkedResults: ({
	linkedResults,
	thirdPartyConfigs,
	partial,
	url,
	position,
	onResultClicked,
	isAllProductSelected,
	onResultContextMenu,
	is3pGroupingEnabled,
	product,
	fallbackType,
}: SimilarLinkedResultsProps) => false | JSX.Element = ({
	linkedResults,
	thirdPartyConfigs,
	partial,
	url,
	position,
	onResultClicked,
	isAllProductSelected = false,
	onResultContextMenu,
	is3pGroupingEnabled,
	product,
	fallbackType,
}: SimilarLinkedResultsProps) => {
	const { formatMessage } = useIntl();
	const isFallbackCase =
		(is3pGroupingEnabled === undefined || is3pGroupingEnabled === false) && isAllProductSelected;
	const shouldShowLinkedResults =
		partial.linkedResults?.length > 0 && (is3pGroupingEnabled || isFallbackCase);

	// expanded linked results for the first 5 results by default
	const shouldExpandLinkedResults =
		position !== undefined && position <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND ? 0 : undefined;
	const similarLinkedResults = linkedResults.filter(
		(linkedResult: { category: string }) =>
			linkedResult.category.toLocaleUpperCase() === Category.SIMILAR,
	);

	return (
		shouldShowLinkedResults && (
			<ExpandList
				defaultExpandedIndex={shouldExpandLinkedResults}
				parentPosition={position}
				buttonType={Category.SIMILAR}
			>
				<Expand
					icon={LinkIcon}
					attachmentCount={similarLinkedResults?.length ?? 0}
					attachmentType="url"
					label={formatMessage(messages.related)}
				>
					<LinkedResults
						{...partial}
						url={url}
						thirdPartyConfigs={thirdPartyConfigs}
						linkedResults={similarLinkedResults}
						onResultClicked={onResultClicked}
						onResultContextMenu={onResultContextMenu}
						position={position}
						product={product}
						fallbackType={fallbackType}
					/>
				</Expand>
			</ExpandList>
		)
	);
};

type ChildLinkedResultsProps = {
	linkedResults: SearchPageNode;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined | null;
	partial: SearchPageNode;
	url: string;
	position?: number;
	onResultClicked: ResultClickedCallback;
	onResultContextMenu?: ResultClickedCallback;
};

export const ChildLinkedResults = ({
	linkedResults,
	thirdPartyConfigs,
	partial,
	url,
	position,
	onResultClicked,
	onResultContextMenu,
}: ChildLinkedResultsProps): any => {
	const { formatMessage } = useIntl();

	const shouldShowLinkedResults = partial.linkedResults && partial.linkedResults.length > 0;

	const shouldExpandLinkedResults =
		position !== undefined && position <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND ? 0 : undefined;
	const childLinkedResults: SearchLinkedResult[] = linkedResults.filter(
		(linkedResult: { category: string }) =>
			linkedResult.category.toLocaleUpperCase() === Category.CHILD,
	);

	return (
		shouldShowLinkedResults &&
		childLinkedResults.length > 0 && (
			<ExpandList
				defaultExpandedIndex={shouldExpandLinkedResults}
				parentPosition={position}
				buttonType={Category.CHILD}
			>
				<Expand
					icon={LinkIcon}
					attachmentCount={childLinkedResults.length}
					attachmentType="url"
					label={formatMessage(messages.relatedPages)}
				>
					<LinkedResults
						{...partial}
						url={url}
						thirdPartyConfigs={thirdPartyConfigs}
						linkedResults={childLinkedResults}
						onResultClicked={onResultClicked}
						onResultContextMenu={onResultContextMenu}
						position={position}
					/>
				</Expand>
			</ExpandList>
		)
	);
};
